import type { IFilter } from "@/api/AppApi/services/filters/interfaces/IGetFilters";
import type { IProductImage } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import type {
  IAttiribs,
  IProductChildMinimizedData,
  IProductMinimizedData,
} from "./interfaces";
import { ICartItemType } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";

export function fillSelectedAttributes(
  attributes: IFilter[],
  startIndex = 0,
  unavailableKeysMap?: IAttiribs,
  outOfStockKeysMap?: IAttiribs,
) {
  const selectedKeys: string[] = [];
  let currentIndex = startIndex;

  const attributesToStartFrom = attributes.filter(
    (_, index) => index >= startIndex,
  );

  for (const attribute of attributesToStartFrom) {
    const unavailableKeys =
      unavailableKeysMap?.[attribute.configurations.key] || [];
    const outOfStockKeys =
      outOfStockKeysMap?.[attribute.configurations.key] || [];

    if (attribute.data?.length === 1) {
      selectedKeys.push(attribute.data[0].key.toString());
      currentIndex += 1;
    } else if (
      attribute.data?.filter(
        (item) =>
          !unavailableKeys.includes(item.key) &&
          !outOfStockKeys.includes(item.key),
      ).length === 1
    ) {
      // Find the one available and in-stock option
      const availableOption = attribute.data.find(
        (item) =>
          !unavailableKeys.includes(item.key) &&
          !outOfStockKeys.includes(item.key),
      );
      if (availableOption) {
        selectedKeys.push(availableOption.key.toString());
        currentIndex += 1;
      } else {
        break;
      }
    } else {
      break;
    }
  }

  return { selectedKeys, currentIndex };
}

export function isAttributesCombinationAvailable(
  attributesCombination: string[],
  lakeMatrix: number[][],
) {
  return lakeMatrix.some((lakeRow) => {
    return attributesCombination.every((attribute) =>
      lakeRow.includes(+attribute),
    );
  });
}

export function findUnavailableAttributes(
  selectedAttributes: IAttiribs,
  product: IProductMinimizedData,
) {
  if (!product.is_configurable || !product.children?.length) {
    return {};
  }

  const attributesCombination = Object.values(selectedAttributes).reduce(
    (prev, curr) => [...prev, ...curr],
    [],
  );
  const { children, attributes_builder } = product;
  const childrenConvertedToAttrCombination = children.map(
    (child) => child.attribute_ids,
  );

  return attributes_builder
    .filter((_, attrIdx) => attrIdx > 0)
    .reduce((prev, curr) => {
      return {
        ...prev,
        [curr.configurations.key]: curr.data
          ?.filter(
            (attrVal) =>
              !isAttributesCombinationAvailable(
                [
                  ...attributesCombination.filter(
                    (item) =>
                      !curr.data?.map((item) => item.key).includes(item),
                  ),
                  attrVal.key,
                ],
                childrenConvertedToAttrCombination,
              ),
          )
          .map((item) => item.key),
      };
    }, {});
}


function findAttributesWhereAllChildren(
  selectedAttributes: IAttiribs,
  product: IProductMinimizedData,
  isChildBlocked: (child: IProductChildMinimizedData) => boolean,
): IAttiribs {
  if (!product.is_configurable || !product.children?.length) {
    return {};
  }
  const { children, attributes_builder } = product;
  return attributes_builder.reduce<IAttiribs>((prev, curr) => {
    const blockedKeys = curr.data
      ?.filter((attrVal) => {
        // Get all children that have this attribute value
        const childrenWithThisAttr = children.filter((child) =>
          child.attribute_ids.includes(+attrVal.key),
        );

        // Get all selected attributes except the current attribute being checked
        const otherSelectedAttrs = Object.entries(selectedAttributes).filter(
          ([key]) => key !== curr.configurations.key,
        );

        // If there are other selected attributes, filter children that match those selections
        let filteredChildren = childrenWithThisAttr;
        if (otherSelectedAttrs.length > 0) {
          filteredChildren = childrenWithThisAttr.filter((child) =>
            otherSelectedAttrs.every(([attrKey, attrValues]) => {
              // Find the attribute in attributes_builder
              const attribute = attributes_builder.find(
                (attr) => attr.configurations.key === attrKey,
              );
              if (!attribute) return true;

              // Check if the child has any of the selected attribute values
              return attrValues.some((val) => {
                const attrData = attribute.data?.find((d) => d.key === val);
                return attrData && child.attribute_ids.includes(+attrData.key);
              });
            }),
          );
        }

        return (
          filteredChildren.length > 0 && filteredChildren.every(isChildBlocked)
        );
      })
      .map((item) => item.key);

    return {
      ...prev,
      [curr.configurations.key]: blockedKeys || [],
    };
  }, {});
}

/** Attribute values whose children are all sold out. */
export function findOutOfStockAttributes(
  selectedAttributes: IAttiribs,
  product: IProductMinimizedData,
  cartItems: ICartItemType[] | undefined,
) {
  return findAttributesWhereAllChildren(selectedAttributes, product, (child) => {
    const currentProductQuantityInCart =
      cartItems
        ?.filter((item) => item.inner_id === child.id)
        .reduce((prev, current) => prev + current.quantity, 0) ?? 0;
    return (
      child?.quantity_on_hand - currentProductQuantityInCart <= 0 ||
      (!!child.min_quantity_in_cart &&
        child.quantity_on_hand - currentProductQuantityInCart <
          child.min_quantity_in_cart)
    );
  });
}

/**
 * Attribute values whose children are all disabled by the backend (`is_disable`).
 * Kept separate from out-of-stock: these are never selectable (even with the
 * stock reminder flow on) and don't get the out-of-stock treatment.
 */
export function findDisabledAttributes(
  selectedAttributes: IAttiribs,
  product: IProductMinimizedData,
) {
  return findAttributesWhereAllChildren(
    selectedAttributes,
    product,
    (child) => !!child.is_disable,
  );
}

/** Union of two attribute-key maps, per attribute key. */
export function mergeAttributeKeys(a: IAttiribs, b: IAttiribs): IAttiribs {
  const merged: IAttiribs = { ...a };
  for (const [key, values] of Object.entries(b)) {
    merged[key] = Array.from(new Set([...(merged[key] ?? []), ...values]));
  }
  return merged;
}

interface IVariantImageSource {
  id: number;
  attribute_ids: number[];
  images: IProductImage[];
}

/**
 * Picks whose gallery to show for the current attribute selection — the
 * configurable product or ONE of its variants (galleries are never merged).
 *
 * - An exact variant (`selectedChild`) wins when it has images; otherwise the
 *   product's own images are used — never another variant's (we can't know
 *   which attribute controls appearance).
 * - On a partial selection, the first variant in API order that matches every
 *   selected value AND has images is used, whatever the unselected attributes.
 * - No selection, or no matching variant with images → the product itself.
 * - A selection that matches EVERY variant narrows nothing (e.g. a single-option
 *   Size like "FS", which the selector auto-picks on load) → the product
 *   itself, so its full all-colours gallery isn't swapped for the first variant's.
 *
 * Generic over attributes (no Color/Size special-casing); it only affects the
 * gallery — price, stock and add-to-cart still come from the exact variant.
 */
export function getVariantImageSource<
  P extends IVariantImageSource,
  C extends IVariantImageSource,
>(
  product: P & { children?: C[] | null },
  selectedValueIds: number[],
  selectedChild?: C | null,
): P | C {
  const hasImages = (item: IVariantImageSource) => !!item.images?.length;
  if (selectedChild) return hasImages(selectedChild) ? selectedChild : product;

  const selected = selectedValueIds.filter(Number.isFinite);
  if (!selected.length) return product;

  const children = product.children ?? [];
  const matching = children.filter((child) =>
    selected.every((id) => child.attribute_ids?.includes(id)),
  );
  if (matching.length === children.length) return product;

  return matching.find(hasImages) ?? product;
}
